import { LitElement, css, html, render } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import * as d3 from 'd3';
import { dom, library } from '@fortawesome/fontawesome-svg-core';
import {
  faMinus,
  faSquare,
  faUserLock,
} from '@fortawesome/pro-solid-svg-icons';
import {
  autoPlacement,
  computePosition,
  offset,
  shift,
} from '@floating-ui/dom';
import {
  FormatComboTierSpendLevelOptions,
  Formatter,
  transformWithFiveParams,
} from '@aptitude/shared-vanilla';

import { variables } from '../variables';

export interface MicroChartTier {
  attainability: string;
  max: number;
  maxPct: number;
  min: number;
  minPct: number;
  order: number;
  tierType: string;
  targetSpendLevel: TargetSpendLevel;
}
interface TargetSpendLevel {
  type: Type;
  dollarMarketShare?: number;
  absoluteDollars?: number;
  unitMarketShare?: number;
  absoluteUnits?: number;
}
interface Type {
  name: string;
  fieldNames: TierFieldNames[];
  operator: 'AND' | 'OR' | undefined | null;
  displayName: string;
}

type TierFieldNames =
  | 'dollarMarketShare'
  | 'unitMarketShare'
  | 'absoluteDollars'
  | 'absoluteUnits';

interface Dimensions {
  width: number;
  height: number;
  margin: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  boundedWidth: number;
  boundedHeight: number;
}

// This gets used to clamp our output to just ten tiers. I really really hate this, but it
// is what was asked for and my objections have been overruled and thus are just impotent
// rantings in the code.
const DISPLAY_TEN = true;

const MIN_BAR_HEIGHT = 1;

(window as any).process = {
  env: {},
};

@customElement('apt-micro-chart')
export class AptMicroChart extends LitElement {
  dimensions: Dimensions = {
    width: 160,
    height: 110,
    margin: {
      top: 10,
      right: 10,
      bottom: 20,
      left: 10,
    },
    boundedWidth: 0,
    boundedHeight: 0,
  };

  @property({ type: Object })
  data!: { tiers: Array<MicroChartTier> };

  @property({ type: Number })
  ydomain?: number;

  static override styles = [
    variables,
    css`
      :host {
        display: block;
        font-family: Neue Helvetica W01, Helvetica, Arial, sans-serif;
      }

      svg {
        color: var(--gray-200);
      }

      rect.negative {
        fill: var(--orange);
      }

      rect.positive {
        fill: var(--green);
      }

      rect.unattainable {
        fill: var(--gray-700);
      }

      /* Both the tooltips are positioned absolute because we want them to take up no space when hidden near the chart. */
      #tooltip {
        min-width: 250px;
        width: max-content;
        position: absolute;
        top: 0;
        left: 0;

        border: 1px solid var(--gray-600);
        border-radius: 10px;

        background-color: var(--white);
        visibility: hidden;
      }

      #tooltip.visible {
        visibility: visible;
      }

      #tooltip .tooltip-header {
        padding: 5px 10px 0 10px;
        text-align: left;
      }

      #tooltip .tooltip-body {
        padding: 5px 10px 5px 10px;
        text-align: left;
      }

      .tooltip-body .price-grid {
        display: flex;
        flex-direction: row;
        align-content: space-between;
        white-space: nowrap;
      }

      .price-grid .price-increase {
        width: 30%;
        text-align: right;
        color: var(--orange);
        font-weight: bold;
      }

      .price-grid .price-saving {
        width: 30%;
        text-align: right;
        color: var(--green);
        font-weight: bold;
      }

      .price-grid .zero-value {
        width: 30%;
        text-align: right;
        color: var(--gray-600);
        font-weight: bold;
      }

      .price-grid .price-description {
        width: 70%;
        text-align: left;
      }

      #legend {
        min-width: 350px;
        max-width: 350px;
        width: max-content;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 100;

        border: 1px solid var(--gray-600);
        border-radius: 10px;

        background-color: var(--white);
        font-size: 14px;
        visibility: hidden;
      }

      #legend.visible {
        visibility: visible;
      }

      #legend .legend-header {
        padding: 5px 10px 0 10px;
        text-align: left;
      }

      #legend .legend-body {
        padding: 5px 10px 5px 10px;
        text-align: left;
      }

      .legend-body .legend-grid {
        display: flex;
        flex-direction: row;
        align-content: space-between;
        align-items: center;
      }

      .legend-grid .legend-symbol {
        width: 14px;
        height: 14px;
        text-align: right;
      }

      .legend-symbol .legend-increase {
        color: var(--orange);
      }

      .legend-symbol .legend-current {
        color: var(--gray-900);
      }

      .legend-symbol .legend-saving {
        color: var(--green);
      }

      .legend-symbol .legend-unattainable {
        color: var(--gray-600);
      }

      .legend-symbol .legend-private {
        color: var(--gray-900);
      }

      .legend-symbol .legend-description {
        align-items: center;
        width: 90%;
        text-align: left;
      }

      .legend-body .legend-footer {
        padding: 5px 10px 5px 10px;
        line-height: 90%;
      }

      g.tick text {
        font-size: 12px;
        fill: var(--gray-600);
      }
    `,
  ];

  override render() {
    return html`<div id="microchart-container"></div>`;
  }

  override firstUpdated(): void {
    library.add(faMinus);
    library.add(faSquare);
    library.add(faUserLock);

    // @ts-expect-error The dom.watch() TS definition indicates no parameters for this. However, when
    // you actually dig into the code, you see that it looks at the arguments after it gets into
    // the code and tries to pull some out if there aren't any (it even supplies an empty object
    // for options if you don't supply one yourself).
    //
    // Note: We have to wait for the first update for the shadow DOM to even be created. Starting
    // the watch now will cause the <i> we inserted to be replaced with an SVG symbol we can reference
    // from out D3.js code.
    dom.watch({
      autoReplaceSvgRoot: this.renderRoot,
      observeMutationsRoot: this.renderRoot,
    });
  }

  override updated() {
    // Regenerate the chart, legend, etc. If you don't have a root element in the component which this is all a child of
    // then the composed styles don't seem to work correctly. They don't get inherited by the tooltip and legend.
    this.generateChart(this.data, this.ydomain);
  }

  createDimensions() {
    this.dimensions.boundedWidth =
      this.dimensions.width -
      this.dimensions.margin.left -
      this.dimensions.margin.right;
    this.dimensions.boundedHeight =
      this.dimensions.height -
      this.dimensions.margin.top -
      this.dimensions.margin.bottom;

    return this.dimensions;
  }

  createDomElements(dimensions: Dimensions) {
    // This clears out any elements which might already have been in place.
    //
    // Note: This is **SUPER** important. If you don't delete these elements you can end up with two or
    // more of the same element inside here. And that can cause problems for your own selectors and those
    // used by other libraries.
    d3.select(this.renderRoot as unknown as Element)
      .selectChild('#microchart-container')
      .selectChildren()
      .remove();

    // <!-- Name symbols with the value of data-fa-symbol -->
    // <i data-fa-symbol="picture-taker" class="fas fa-camera"></i>;
    const rootDiv = d3
      .select(this.renderRoot as unknown as Element)
      .selectChild('#microchart-container');

    rootDiv
      .append('i')
      .attr('data-fa-symbol', 'userlock')
      .attr('class', 'fas fa-user-lock');

    d3.select(this.shadowRoot as unknown as Element)
      .selectChildren('svg#microchart')
      .remove();

    // This is the outermost container for the chart. It also sets the current color for everything else
    // that uses it.
    const svg = rootDiv
      .append('svg')
      .attr('id', 'microchart')
      .attr('width', dimensions.width)
      .attr('height', dimensions.height);

    // But there's an inner container which allows us to have margin space for axes, labels, etc.
    const bounds = svg
      .append('g')
      .style(
        'transform',
        `translate(${dimensions.margin.left}px, ${dimensions.margin.top}px)`,
      );

    // Adding a hidden div to display tooltips
    const tooltip = rootDiv
      .append('div')
      .attr('role', 'tooltip')
      .attr('id', 'tooltip');

    // Adding hidden div to display legend
    const legend = rootDiv
      .append('div')
      .attr('role', 'legend')
      .attr('id', 'legend');

    return {
      tooltip,
      legend,
      svg,
      bounds,
    };
  }

  createScales(
    data: { tiers: Array<MicroChartTier> },
    dimensions: Dimensions,
    yDomain: number | null,
  ) {
    const minPct = d3.min(data.tiers, (d: MicroChartTier) => d.minPct) || 0;
    const maxPct = d3.max(data.tiers, (d: MicroChartTier) => d.maxPct) || 0;
    let maxAbsPct = Math.max(Math.abs(minPct), Math.abs(maxPct));

    if (yDomain) {
      maxAbsPct = yDomain;
    }

    const xScale = d3
      .scaleBand()
      .domain(
        DISPLAY_TEN
          ? ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
          : data.tiers?.map((d) => d.order.toString(10)),
      )
      .range([0, dimensions.boundedWidth])
      .paddingInner(0.25)
      .paddingOuter(0.25);

    const yScale = d3
      .scaleLinear()
      .domain([-maxAbsPct, maxAbsPct])
      .range([0, dimensions.boundedHeight])
      .nice();

    const colorScale = d3
      .scaleThreshold<number, string>()
      .domain([0, 0.5, 1])
      .range(['positive', 'negative', 'negative']);

    return { xScale, yScale, colorScale };
  }

  // When the bar is entirely above or below the zero line, we only return an array containing a single
  // object, and when it crosses, we return two.
  splitAtZero(tier: MicroChartTier) {
    if (tier.maxPct <= 0 || tier.minPct >= 0) {
      return [tier];
    } else {
      // We must be crossing the zero axis, so we'll display two separate values, one above and one below.
      return [
        Object.assign({}, tier, { minPct: tier.minPct, maxPct: 0 }),
        Object.assign({}, tier, { minPct: 0, maxPct: tier.maxPct }),
      ];
    }
  }

  drawPeripheralsBeforeData(
    domElements: { bounds: any },
    data: unknown,
    dimensions: Dimensions,
    scale: {
      xScale: d3.ScaleBand<string>;
      yScale: d3.ScaleLinear<number, number>;
    },
  ) {
    // Add a backdrop to the graph.
    domElements.bounds
      .append('rect')
      .attr('width', dimensions.boundedWidth)
      .attr('height', dimensions.boundedHeight)
      .attr('fill', 'white');

    // Top line.
    domElements.bounds
      .append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', dimensions.boundedWidth)
      .attr('y2', 0)
      .attr('stroke', 'currentColor');

    // y-Axis zero line.
    domElements.bounds
      .append('line')
      .attr('x1', 0)
      .attr('y1', scale.yScale(0))
      .attr('x2', dimensions.boundedWidth)
      .attr('y2', scale.yScale(0))
      .attr('stroke', 'currentColor');
  }

  tooltipPosition = {
    middleware: [autoPlacement(), offset(6), shift({ padding: 20 })],
  };

  drawData(
    domElements: { tooltip: any; bounds: any },
    data: { tiers: Array<MicroChartTier> },
    scale: {
      xScale: d3.ScaleBand<string>;
      yScale: d3.ScaleLinear<number, number>;
      colorScale: d3.ScaleThreshold<number, string>;
    },
  ) {
    // This actually took a while to figure out. There are two bindings of data to graph elements.
    // The first one is tiers to SVG group elements.
    const tiers = domElements.bounds.selectAll('g').data(data.tiers).join('g');

    // Then there's a second binding. Each tier has either one or two objects inside it. Each of
    // those then binds to a SVG rect element.
    tiers
      .selectAll('rect')
      .data((d: MicroChartTier) => this.splitAtZero(d))
      .join('rect')
      .attr('class', (d: MicroChartTier) => {
        // Unattainable tiers should be colored differently.
        if (d.attainability === 'ATTAINABLE') {
          return `${scale.colorScale(d.minPct)}`;
        } else {
          return 'unattainable';
        }
      })
      .attr('x', (d: MicroChartTier) => {
        return scale.xScale(d.order.toString());
      })
      .attr('width', scale.xScale.bandwidth())
      .transition()
      .attr('y', (d: MicroChartTier) => {
        const barHeight = Math.abs(
          scale.yScale(d.maxPct) - scale.yScale(d.minPct),
        );

        if (barHeight <= MIN_BAR_HEIGHT) {
          return scale.yScale(-d.minPct) - barHeight - 0.5 * MIN_BAR_HEIGHT;
        } else {
          return scale.yScale(-d.minPct) - barHeight;
        }
      })
      .attr('height', (d: MicroChartTier) =>
        Math.max(
          Math.abs(scale.yScale(d.maxPct) - scale.yScale(d.minPct)),
          MIN_BAR_HEIGHT,
        ),
      );

    // And yet another binding! This adds a another set of transparent bars w/ one per tier.
    // This transparent bar is where we can attach tooltips. Because it is full height, it makes a
    // better tooltip target than the (often) quite small bars showing the actual data for a tier.
    const yRange = scale.yScale.range();
    domElements.bounds
      .selectAll('g')
      .data(data.tiers)
      .join('g')
      .append('rect')
      .attr('fill', 'transparent')
      .attr('x', (d: MicroChartTier) => scale.xScale(d.order.toString()))
      .attr('width', scale.xScale.bandwidth())
      .attr('y', yRange[0])
      .attr('height', yRange[1])
      .attr('tier-order', (d: MicroChartTier) => d.order.toString())
      .on('mouseover', (event: MouseEvent, d: MicroChartTier) => {
        // We need these ids to handle screens with multiple charts
        const microchart = this.renderRoot;
        const tooltipEl = microchart?.querySelector('#tooltip') as HTMLElement;
        render(this.buildTooltipHtml(d), tooltipEl);

        // Shows the tooltip appear on mouseover:
        domElements.tooltip.classed('visible', true);

        const hoverTarget = this.renderRoot.querySelector(
          `[tier-order="${d.order}"]`,
        );

        if (hoverTarget) {
          computePosition(hoverTarget, tooltipEl, this.tooltipPosition).then(
            ({ x, y }) => {
              Object.assign(tooltipEl.style, {
                left: `${x}px`,
                top: `${y}px`,
              });
            },
          );
        }
      })
      .on('mouseout', () => {
        // Hides the tooltip on mouseout:
        domElements.tooltip.classed('visible', false);
      });
  }

  drawPeripheralsAfterData(
    domElements: { legend: any; bounds: any },
    data: { tiers: Array<MicroChartTier> },
    dimensions: Dimensions,
    scale: {
      xScale: d3.ScaleBand<string>;
      yScale: d3.ScaleLinear<number, number>;
    },
  ) {
    // Figure out if we have some private tiers.
    const somePrivate = data.tiers?.some(
      (tier: MicroChartTier) => tier.tierType === 'PRIVATE',
    );

    // x-Axis line and ticks.
    const xAxis = d3
      .axisBottom(scale.xScale)
      .tickSize(0)
      .tickValues(
        scale.xScale.domain().filter((d, i) => {
          return i < data.tiers.length;
        }),
      );

    domElements.bounds
      .append('g')
      .attr('transform', `translate(0, ${dimensions.boundedHeight})`)
      .call(xAxis)
      // Transparent bar for legend hover over on x-axis
      .append('rect')
      .attr('fill', 'transparent')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', dimensions.boundedWidth)
      // Font-size is 12, make the bar height an even 15
      .attr('height', 15)
      .attr('legend-hover-target', '')
      .on('mouseover', (event: MouseEvent) => {
        const microchart = this.renderRoot;
        const legendEl = microchart?.querySelector('#legend') as HTMLElement;

        render(this.buildLegendHtml(somePrivate), legendEl);

        // Shows the legend appear on mouseover:
        domElements.legend.classed('visible', true);

        const hoverTarget = this.renderRoot.querySelector(
          `[legend-hover-target]`,
        );

        if (hoverTarget) {
          computePosition(hoverTarget, legendEl, this.tooltipPosition).then(
            ({ x, y }) => {
              Object.assign(legendEl.style, {
                left: `${x}px`,
                top: `${y}px`,
              });
            },
          );
        }
      })
      .on('mouseout', () => {
        // Hides the legend on mouseout:
        domElements.legend.classed('visible', false);
      });

    // Get the text labels for the ticks. For specific ones, add an icon reference and then delete the text.
    // We show a user + lock icon to indicate a private tier and no text label at all.
    const privateTiers = d3
      .select(this.renderRoot as unknown as Element)
      .selectAll('g.tick')
      .filter((d, i, nodes) => {
        return data.tiers[i].tierType === 'PRIVATE';
      });

    privateTiers
      .append('use')
      .attr('xlink:href', '#userlock')
      .attr('transform', `translate(-7, 2) scale(0.09, 0.09)`)
      .attr('color', 'black');

    privateTiers.select('text').remove();
  }

  generateChart(
    data: { tiers: Array<MicroChartTier> },
    yDomain: number | null = null,
  ) {
    if (data?.tiers.length > 0) {
      // This is a significantly revised version of the code which is broken into five steps (getting
      // the data itself is a sixth step). Each step is pretty self explanatory and isolated enough
      // that you can follow this code pretty well.
      const dimensions = this.createDimensions();

      const domElements = this.createDomElements(dimensions);
      const scales = this.createScales(data, dimensions, yDomain);

      this.drawPeripheralsBeforeData(domElements, data, dimensions, scales);
      this.drawData(domElements, data, scales);
      this.drawPeripheralsAfterData(domElements, data, dimensions, scales);

      return domElements.svg.node();
    } else {
      return null;
    }
  }

  buildTooltipHtml(d: MicroChartTier) {
    // Grab Tier Spend Descriptor for header
    const formatComboTierOptions: FormatComboTierSpendLevelOptions = {
      isPrivate: d.tierType === 'PRIVATE',
      order: d.order,
      sizeOfSpendTypeLabel: 'SHORT_DISPLAY_NAME',
      truncateValues: true,
    };

    const tooltipDescriptorText = transformWithFiveParams(
      d.targetSpendLevel,
      formatComboTierOptions,
      Formatter.currency,
      Formatter.percent,
      (value: number | null) =>
        new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
          value as number,
        ),
    );

    const tooltipDescriptor = html`${tooltipDescriptorText}`;
    const tooltipHeader = html`
      <div class="tooltip-header">
        ${tooltipDescriptor}
        <hr style="margin: 2px 0;" />
      </div>
    `;

    // Make this easier to read, set up max and min for formatting
    const maxClass = d.max > 0 ? 'price-increase' : 'zero-value';

    const numberFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
    const max = numberFormatter.format(d.max > 0 ? d.max : 0);
    const minClass = d.min < 0 ? 'price-saving' : 'zero-value';
    const min = numberFormatter.format(d.min < 0 ? d.min * -1 : 0);

    // Show a different tooltip if the tier is unattainable
    const tooltipBody =
      d.attainability === 'ATTAINABLE'
        ? html`
            <div class="tooltip-body">
              <small class="muted">
                <div class="price-grid">
                  <div class="${maxClass}">${max}</div>
                  <div class="price-description">
                    &nbsp;Potential Price Increases
                  </div>
                </div>
                <div class="price-grid">
                  <div class="${minClass}">${min}</div>
                  <div class="price-description">
                    &nbsp;Potential Price Savings
                  </div>
                </div>
              </small>
            </div>
          `
        : html`
            <div class="tooltip-body">
              This tier cannot be achieved. Given the supplier's coverage of
              your spend and or units.
            </div>
          `;

    return html`${tooltipHeader}${tooltipBody}`;
  }

  buildLegendHtml(somePrivate: boolean) {
    let privateLegend = html``;

    if (somePrivate) {
      privateLegend = html`<div class="legend-grid">
        <div class="legend-symbol">
          <i
            class="fa-solid fa-user-lock legend-private"
            aria-hidden="true"
          ></i>
        </div>
        <div class="legend-description">
          <small class="muted">&nbsp;Private Tier</small>
        </div>
      </div>`;
    }

    return html`
      <div class="legend-header">Range of financial outcomes at each tier</div>
      <div class="legend-body">
        <div class="legend-grid">
          <div class="legend-symbol">
            <i class="fa fa-square legend-increase" aria-hidden="true"></i>
          </div>
          <div class="legend-description">
            <small class="muted">&nbsp;Potential Price Increases</small>
          </div>
        </div>
        <div class="legend-grid">
          <div class="legend-symbol">
            <i class="fa fa-minus legend-current" aria-hidden="true"></i>
          </div>
          <div class="legend-description">
            <small class="muted">&nbsp;Current Price</small>
          </div>
        </div>
        <div class="legend-grid">
          <div class="legend-symbol">
            <i class="fa fa-square legend-saving" aria-hidden="true"></i>
          </div>
          <div class="legend-description">
            <small class="muted">&nbsp;Potential Price Savings</small>
          </div>
        </div>
        <div class="legend-grid">
          <div class="legend-symbol">
            <i class="fa fa-square legend-unattainable" aria-hidden="true"></i>
          </div>
          <div class="legend-description">
            <small class="muted"
              >&nbsp;Not Attainable (offer coverage and/or tier
              requirements)</small
            >
          </div>
        </div>
        ${privateLegend}
        <div class="legend-footer">
          <small class="muted">
            Each bars height shows the range of product decisions available
            while ensuring compliance with tier requirements.
          </small>
        </div>
      </div>
    `;
  }
}
